import { useState } from "react";
import { MotiView } from "moti";
import { Pressable, StyleSheet, View } from "react-native";
import { Bell, BookOpen, ChevronDown, ChevronUp, CircleCheck, GraduationCap } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import CustomText from "../../../../../components/common/general/CustomText/CustomText";
import CustomSwitch from "../../../../../components/common/general/CustomSwitch/CustomSwitch";
import { colors, radii, spacing, typographyScale } from "../../../../../styles/design-system";

type NotificationCategory = {
  key: string;
  title: string;
  description: string;
  icon: LucideIcon;
  iconBackgroundColor: string;
  iconColor: string;
};

type NudgesPreferencesProps = {
  categoryToggles: Record<string, boolean>;
  animationsEnabled: boolean;
  onUserInteraction: () => void;
  onCategoryToggle: (key: string, nextValue: boolean) => void;
};

const SUBMENU_ANIMATION_DURATION_MS = 220;
const NOTIFICATION_CATEGORIES: NotificationCategory[] = [
  {
    key: "courseUpdates",
    title: "Course Updates",
    description: "Homework, tests",
    icon: BookOpen,
    iconBackgroundColor: colors.blue[100],
    iconColor: colors.blue[600],
  },
  {
    key: "facultyUpdates",
    title: "Faculty Updates",
    description: "Grades, announcements",
    icon: GraduationCap,
    iconBackgroundColor: colors.purple[100],
    iconColor: colors.purple[600],
  },
];
const CATEGORY_SUBMENU_ITEMS_BY_KEY: Record<string, string[]> = {
  courseUpdates: ["Homework due reminders", "Test & quiz alerts"],
  facultyUpdates: ["Grades posted with feedback", "Course announcements"],
};

export const NudgesPreferences = ({
  categoryToggles,
  animationsEnabled,
  onUserInteraction,
  onCategoryToggle,
}: NudgesPreferencesProps) => {
  const [expandedCategoryKey, setExpandedCategoryKey] = useState<string | null>(null);
  const [submenuHeights, setSubmenuHeights] = useState<Record<string, number>>({});
  const shouldAnimate = animationsEnabled;
  const submenuAnimationDuration = shouldAnimate ? SUBMENU_ANIMATION_DURATION_MS : 0;

  const enableAnimations = () => {
    onUserInteraction();
  };

  const handleCategoryExpandToggle = (key: string) => {
    setExpandedCategoryKey((previous) => (previous === key ? null : key));
  };

  return (
    <View style={[styles.sectionContainer, styles.categorySectionContainer]}>
      <View style={styles.categorySectionHeaderContent}>
        <View style={styles.sectionHeader}>
          <Bell size={18} color={colors.grey[500]} />
          <CustomText text="What to notify you about" useTranslationText={false} style={styles.sectionHeaderTitle} />
        </View>
        <CustomText
          text="Toggle categories on or off. Expand to see what's included."
          useTranslationText={false}
          style={styles.sectionSubtitle}
        />
      </View>

      {NOTIFICATION_CATEGORIES.map((category) => {
        const Icon = category.icon;
        const categorySubmenuItems = CATEGORY_SUBMENU_ITEMS_BY_KEY[category.key] ?? [];
        const categoryIsExpanded = expandedCategoryKey === category.key;
        const categorySubmenuHeight = submenuHeights[category.key] ?? 0;
        const categorySubmenuAnimatedHeight = categoryIsExpanded ? categorySubmenuHeight : 0;
        const categorySubmenuAnimatedOpacity = categoryIsExpanded ? 1 : 0;

        return (
          <View key={category.key}>
            <Pressable
              role="button"
              onPress={() => {
                enableAnimations();
                handleCategoryExpandToggle(category.key);
              }}
              style={({ hovered }) => [styles.categoryRow, hovered && styles.categoryRowHover]}
              accessibilityLabel={`${category.title} details ${categoryIsExpanded ? "collapse" : "expand"}`}>
              <View style={styles.categoryInfo}>
                <View style={[styles.categoryIcon, { backgroundColor: category.iconBackgroundColor }]}>
                  <Icon size={16} color={category.iconColor} />
                </View>
                <View style={styles.categoryText}>
                  <CustomText text={category.title} useTranslationText={false} style={styles.categoryTitle} />
                  <CustomText
                    text={category.description}
                    useTranslationText={false}
                    style={styles.categoryDescription}
                  />
                </View>
              </View>
              <View style={styles.categoryControls}>
                <CustomSwitch
                  value={categoryToggles[category.key] ?? false}
                  onValueChange={(nextValue) => {
                    enableAnimations();
                    onCategoryToggle(category.key, nextValue);
                  }}
                  accessibilityLabel={`${category.title} notifications`}
                />
                <View style={styles.expandButton}>
                  {categoryIsExpanded ? (
                    <ChevronUp size={18} color={colors.grey[500]} />
                  ) : (
                    <ChevronDown size={18} color={colors.grey[500]} />
                  )}
                </View>
              </View>
            </Pressable>

            {categorySubmenuItems.length > 0 ? (
              <MotiView
                animate={
                  shouldAnimate
                    ? {
                        height: categorySubmenuAnimatedHeight,
                        opacity: categorySubmenuAnimatedOpacity,
                      }
                    : undefined
                }
                transition={shouldAnimate ? { type: "timing", duration: submenuAnimationDuration } : undefined}
                style={[
                  styles.categorySubmenuAnimatedContainer,
                  !shouldAnimate && {
                    height: categorySubmenuAnimatedHeight,
                    opacity: categorySubmenuAnimatedOpacity,
                  },
                ]}>
                <View
                  style={styles.categorySubmenu}
                  onLayout={(event) => {
                    const { height } = event.nativeEvent.layout;
                    setSubmenuHeights((previous) => {
                      if (previous[category.key] === height) {
                        return previous;
                      }

                      return {
                        ...previous,
                        [category.key]: height,
                      };
                    });
                  }}>
                  <CustomText
                    text="When enabled, you'll receive:"
                    useTranslationText={false}
                    style={styles.categorySubmenuTitle}
                  />
                  <View style={styles.categorySubmenuItems}>
                    {categorySubmenuItems.map((item) => (
                      <View key={item} style={styles.categorySubmenuItem}>
                        <CircleCheck size={14} color={colors.success[600]} />
                        <CustomText text={item} useTranslationText={false} style={styles.categorySubmenuItemText} />
                      </View>
                    ))}
                  </View>
                </View>
              </MotiView>
            ) : null}
          </View>
        );
      })}
    </View>
  );
};

const styles = StyleSheet.create({
  sectionContainer: {
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: radii.lg,
    padding: spacing.lg,
    position: "relative",
    overflow: "visible",
  },
  categorySectionContainer: {
    padding: spacing.none,
  },
  categorySectionHeaderContent: {
    paddingTop: spacing.lg,
    paddingBottom: spacing.sm,
    paddingHorizontal: spacing.lg,
  },
  sectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
  },
  sectionHeaderTitle: {
    fontSize: typographyScale.textBase.fontSize,
    lineHeight: typographyScale.textBase.lineHeight,
    fontWeight: "600",
    color: colors.grey[800],
  },
  sectionSubtitle: {
    color: colors.grey[500],
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    marginTop: spacing.xxs,
  },
  categoryRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.lg,
    borderTopWidth: 1,
    borderTopColor: colors.grey[200],
  },
  categoryRowHover: {
    backgroundColor: colors.grey[25],
  },
  categoryInfo: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  categoryIcon: {
    width: spacing.xl,
    height: spacing.xl,
    borderRadius: radii.full,
    alignItems: "center",
    justifyContent: "center",
  },
  categoryText: {
    flex: 1,
  },
  categoryTitle: {
    color: colors.grey[800],
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    fontWeight: "500",
  },
  categoryDescription: {
    color: colors.grey[500],
    fontSize: typographyScale.textXs.fontSize,
    lineHeight: typographyScale.textXs.lineHeight,
  },
  categoryControls: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xxs,
    marginLeft: spacing.sm,
  },
  expandButton: {
    width: spacing.lg,
    height: spacing.lg,
    alignItems: "center",
    justifyContent: "center",
  },
  categorySubmenu: {
    borderTopWidth: 1,
    borderTopColor: colors.grey[200],
    paddingTop: spacing.xs,
    paddingBottom: spacing.sm,
    paddingHorizontal: spacing.lg,
  },
  categorySubmenuAnimatedContainer: {
    overflow: "hidden",
    backgroundColor: colors.grey[50],
  },
  categorySubmenuTitle: {
    color: colors.grey[500],
    fontSize: typographyScale.textXs.fontSize,
    lineHeight: typographyScale.textXs.lineHeight,
    marginBottom: spacing.xs,
  },
  categorySubmenuItems: {
    gap: spacing.xs,
  },
  categorySubmenuItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
  },
  categorySubmenuItemText: {
    color: colors.grey[700],
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    fontWeight: "400",
  },
});
